import { displayErrors, parseNotifications } from "common/helpers";
import axios from "axios";
import queryString from "query-string";
import { gridParams } from "common/enums";
import { router } from "router/router";
import { ErrorResponse } from "./types";

const INTERNAL_ERROR_MESSAGE = 'Internal server error. Please, contact system administrator.';

export const wrapRequest = async <T>({ handler, showNotifications }: {
    handler: () => Promise<{ data: T & ErrorResponse }>;
    showNotifications: boolean;
}): Promise<{ data?: T; errors?: string[]; warnings?: string[]; infos?: string[] }> => {
    try {
        const response = await handler();

        showNotifications && parseNotifications({
            warnings: response.data?.warnings,
            errors: response.data?.errors,
            infos: response.data?.infos
        });

        return { data: response.data };
    } catch (reason) {
        if (axios.isAxiosError(reason) && reason.response) {
            const { status } = reason.response;

            handleStatusCode(status);

            if (status >= 400 && status < 500 && status !== 403) {
                const err = reason.response?.data as ErrorResponse ?? {};

                const errors = (err.errors || [err.error]) ?? [];
                const warnings = (err.validationErrors || err.warnings) ?? [];
                const infos = err.infos ?? [];


                showNotifications && parseNotifications({ errors, warnings, infos })

                return { errors, warnings, infos };
            };

            if (status >= 500) {
                showNotifications && displayErrors([INTERNAL_ERROR_MESSAGE]);
                return { errors: [INTERNAL_ERROR_MESSAGE] };
            }
        }

        throw reason;
    }
};

export function handleStatusCode(status: number) {
    if (status === 403) window.location.href = "/forbidden";
};

export interface GroupedByCategory<TItem> {
    categoryName: string;
    items: TItem[];
}

/**
 * Groups a flat list into tree-view categories, used by DashboardViewer and DashboardManagement.
 */
export function treeViewGroupItemsBy<TItem>(list: TItem[], groupKey: keyof TItem): GroupedByCategory<TItem>[] {
    const map = new Map<string, TItem[]>();

    list.forEach(item => {
        const keyValue = item[groupKey];
        const groupValues: unknown[] = Array.isArray(keyValue) ? keyValue : [keyValue];

        groupValues.forEach(groupNameRaw => {
            const categoryName = String(groupNameRaw ?? '');

            let items = map.get(categoryName);

            if (!items) {
                items = [];
                map.set(categoryName, items);
            }

            items.push(item);
        });
    });

    return Array.from(map, ([categoryName, items]) => ({ categoryName, items }));
};

export type PageParams = Record<string, string | number | readonly (string | number | null)[] | null | undefined>;
const queryStringConfig = { arrayFormat: 'comma' } as const;

const currentSearch = () => router.state.location.search;

const replaceSearch = (search: string) => void router.navigate({ search }, { replace: true });

export function getPageParamsByKey(key: string): {
    pagesQueryParams: string[];
    pageQueryParams: PageParams;
} {
    const pagesQueryParams = currentSearch().replace('?', '').split('|').filter(Boolean);
    const searchArr = pagesQueryParams.map((group) => queryString.parse(group, queryStringConfig));

    return {
        pagesQueryParams,
        pageQueryParams: searchArr.find((item) => item[gridParams.pageFilter] === key) ?? { [gridParams.pageFilter]: key },
    };
}

export function redirectWithSearchParams(newPageParamsArray: PageParams[]): void {
    const pageFilter = newPageParamsArray.find((item) => item[gridParams.pageFilter])?.[gridParams.pageFilter];

    if (!pageFilter) {
        return;
    }

    const withNewParams = (pageParamsObject: PageParams): PageParams => newPageParamsArray.reduce((acc, item) => {
        const [key, value] = Object.entries(item)[0];
        acc[key] = value;

        return acc;
    }, pageParamsObject);

    const search = currentSearch();

    if (search.length <= 1) {
        replaceSearch(`?${queryString.stringify(withNewParams({}), queryStringConfig)}`);
        return;
    }

    const pageParamsStringArray = search.replace('?', '').split('|').map((group) => {
        const pageParamsObject = queryString.parse(group, queryStringConfig);

        return queryString.stringify(
            pageParamsObject[gridParams.pageFilter] === pageFilter ? withNewParams(pageParamsObject) : pageParamsObject,
            queryStringConfig
        );
    });

    replaceSearch(`?${pageParamsStringArray.join('|')}`);
}

export function doRedirect(pagesQueryParams: string[], newPageParams: PageParams): void {
    if (pagesQueryParams.length === 0) {
        replaceSearch(`?${queryString.stringify(newPageParams, queryStringConfig)}`);
        return;
    }

    const groups = pagesQueryParams.map((group) =>
        queryString.parse(group, queryStringConfig)[gridParams.pageFilter] === newPageParams[gridParams.pageFilter]
            ? queryString.stringify(newPageParams, queryStringConfig)
            : group
    );

    replaceSearch(`?${groups.join('|')}`);
}